import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { ModuleTopNav } from './ModuleTopNav'
import { SkipLink } from './SkipLink'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { NotificationsLive } from '@/features/notifications/LiveAnnouncer'
import { ShiftEndBanner } from '@/features/handovers/components/ShiftEndBanner'
import { SessionTimeoutModal } from '@/features/auth/SessionTimeoutModal'
import { OnboardingTour } from '@/features/databus/OnboardingTour'
import { TenantSelectorBand } from '@/features/platform/components/TenantSelectorBand'

/**
 * The two-column analyst workspace.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ Header (search · breadcrumbs · bell · user)  │
 *   ├────────┬─────────────────────────────────────┤
 *   │ Side   │ ModuleTopNav (pilot, self-hides)    │
 *   │ nav    ├─────────────────────────────────────┤
 *   │        │  <Outlet />  ← id="page-content"   │
 *   │        │                                     │
 *   └────────┴─────────────────────────────────────┘
 *
 * WCAG 2.4.1 (Bypass Blocks) strategy:
 *   • SkipLink targets #main-content (the <main> element) which lets
 *     keyboard users jump past the Header and Sidebar.
 *   • ModuleTopNav (inside <main>) carries an internal "Skip module
 *     navigation" link targeting #page-content, so users landing in
 *     <main> can bypass the module bar when it is visible.
 *   • The outer grid rows are unchanged (grid-rows-[auto_1fr]) so the
 *     layout stays full-screen without adding a third row.
 *
 * Below the lg breakpoint (1024px) the sidebar collapses to an
 * off-canvas drawer toggled by a hamburger button on the header.
 * Sidebar nav clicks auto-close the drawer.
 *
 * The shell never unmounts on navigation — feature routes render into the
 * `<Outlet />`. WebSocket subscriptions and notification badge live here so
 * they persist across screens.
 */
export function AppShell() {
  const [navOpen, setNavOpen] = useState(false)
  return (
    <div className="grid h-screen grid-cols-1 grid-rows-[auto_1fr] bg-bg text-fg lg:grid-cols-[16rem_1fr]">
      {/* WCAG 2.4.1 Bypass Blocks. Visually hidden until focused so
          keyboard users can jump straight past the header + sidebar
          to the route content. */}
      <SkipLink />
      <Header
        className="col-span-full lg:col-span-2"
        onToggleNav={() => setNavOpen((v) => !v)}
        navOpen={navOpen}
      />
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <main id="main-content" tabIndex={-1} className="overflow-auto bg-muted/30">
        <ErrorBoundary>
          <ShiftEndBanner />
          {/* Phase 3 (tenant-view switcher): persistent amber banner shown when a
              platform admin has selected a tenant to view. Renders nothing for
              tenant-scoped users (TenantSelectorBand gates on useIsPlatformAdmin).
              The global variant only appears when a tenant is selected so normal
              platform-mode navigation is unaffected. */}
          <TenantSelectorBand variant="global" />
          {/* Two-level navigation pilot: module top submenu (self-hides when flag off
              or current route is not part of a configured module).
              Carries an internal skip link (#page-content) so keyboard users who
              already landed in <main> can bypass the module bar. */}
          <ModuleTopNav />
          {/* id="page-content" is the a11y skip target for ModuleTopNav's internal
              skip link. Keyboard users activate "Skip module navigation" here. */}
          <div id="page-content" className="mx-auto w-full max-w-screen-2xl p-4 sm:p-6">
            <Outlet />
          </div>
        </ErrorBoundary>
      </main>
      <NotificationsLive />
      <SessionTimeoutModal />
      <OnboardingTour />
    </div>
  )
}
